import React, { useEffect, useState } from "react";
import { ProColumns } from "@ant-design/pro-table/es/typing";
import clsx from "clsx";
import dayjs from "dayjs";
import { Badge, Button, Select, Tag } from "antd";
import {
  ExportOutlined,
  HomeOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import Table, { TableProps } from "../../Common/Table";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useBreadcrumbs } from "../../../context/breadcrumbs";
import { RequestData, ParamsType } from "@ant-design/pro-components";
import { SortOrder } from "antd/es/table/interface";
import {
  useUsersTableGet,
  UserTableRow,
  UserStatus,
} from "../../../hooks/api/users";
import { useVaadTableGet, useVaadGetById } from "../../../hooks/api/vaads";
import { SubscriptionStatusDisplay } from "../Vaadim/SinglePage/VaadTabs/MembersTab";
import { LazySelect } from "../../Common/SelectLazyLoader";
import {
  queryFilterParams,
  getSorterParams,
  getUserRequestPath,
} from "../../../utils";
import {
  UserPlanDisplay,
  UserRoleDisplay,
  UserStatusDisplay,
} from "../../../enums/user";
import { UserStatusType, useAuth, UserRoleType } from "../../../store/auth";
import { dateTimeFormat } from "../../../contstant";
import { IconExport } from "../../Common/Icon";
import { useUsersExportDownload } from "../../../utils/exportMembers";
import CreateModal from "./Create";

import styles from "./index.module.scss";

export enum UserStatusColor {
  "new_lead" = "blue",
  "form_sent" = "cyan",
  "confirmed" = "geekblue",
  "invited" = "purple",
  "active" = "green",
  "deactivated" = "orange",
  "inactive" = "red",
  "archived" = "gray",
  // "Deleted" = "gray",
}

export function getUserStatusColor(status: UserStatus): string {
  // @ts-ignore take a color from the list
  return UserStatusColor[status?.toLowerCase()] || "rgba(0,0,0,.15)";
}

const SubscriptionStatusFilter = {
  active: { text: "Active" },
  trialing: { text: "Trialing" },
  incomplete: { text: "Incomplete" },
  incomplete_expired: { text: "Incomplete expired" },
  past_due: { text: "Past due" },
  canceled: { text: "Canceled" },
  unpaid: { text: "Unpaid" },
  paused: { text: "Paused" },
  payment_failed: { text: "Payment failed" },
} as const;

const columns = (
  t: TFunction,
  vaadTableGet: ReturnType<typeof useVaadTableGet>,
  initialVaadIdOptions?: { label: string; value: string }[],
): ProColumns<UserTableRow>[] => [
    {
      title: "Full Name",
      dataIndex: "fullName",
      key: "fullName",
      sorter: true,
      hideInSearch: true,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      sorter: false,
      hideInSearch: true,
    },
    {
      title: t("email"),
      dataIndex: "email",
      key: "email",
      hideInSearch: true,
      sorter: false,
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      sorter: false,
      hideInSearch: true,
    },
    {
      title: "Vaad Name",
      dataIndex: "vaadName",
      key: "vaadName",
      sorter: false,
      hideInSearch: true,
      render: (_, record) => (
        <div className={styles.membershipCell}>
          {record.memberships?.map((e, index) => (
            <div key={e.id || index} className={styles.membershipRow}>
              {e.vaadName}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "Vaad ID",
      dataIndex: "vaadID",
      key: "vaadID",
      sorter: true,
      hideInSearch: true,
      render: (_, record) => (
        <div className={styles.membershipCell}>
          {record.memberships?.map((e, index) => (
            <div key={e.id || index} className={styles.membershipRow}>
              {e.vaadId || "-"}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "User status",
      dataIndex: "status",
      key: "status",
      sorter: false,
      valueEnum: UserStatusDisplay,
      render: (_, record) => (
        <div className={styles.membershipCell}>
          {record.memberships?.map((e, index) => (
            <div key={e.id || index} className={styles.membershipRow}>
              <Badge
                color={getUserStatusColor(e.userStatus)}
                text={
                  <span className="capitalize-first-letter">
                    {e.userStatus.replace(/_/g, " ")}
                  </span>
                }
              />
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "Subscription Status",
      dataIndex: "subscriptionStatus",
      key: "subscriptionStatus",
      sorter: false,
      valueEnum: SubscriptionStatusFilter,
      render: (_, record) => (
        <div className={styles.membershipCell}>
          {record.memberships?.map((e, index) => (
            <div key={e.id || index} className={styles.membershipRow}>
              {e.subscriptionStatus ? (
                <Badge
                  color={
                    SubscriptionStatusDisplay[
                      e.subscriptionStatus as keyof typeof SubscriptionStatusDisplay
                    ]?.color || "rgba(0,0,0,.15)"
                  }
                  text={
                    SubscriptionStatusDisplay[
                      e.subscriptionStatus as keyof typeof SubscriptionStatusDisplay
                    ]?.text ?? (
                      <span className="capitalize-first-letter">
                        {e.subscriptionStatus.replace(/_/g, " ")}
                      </span>
                    )
                  }
                />
              ) : (
                "-"
              )}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "Vaad Name",
      dataIndex: "vaadName",
      key: "vaadNameSearch",
      hideInTable: true,
      valueType: "select",
      renderFormItem: (_, props) => (
        <LazySelect
          {...props}
          fetchOptions={async ({ search, page, pageSize }) => {
            const res = await vaadTableGet.fetch({ page, pageSize, search });
            return (res?.data || []).filter((item) => item.name);
          }}
          mapOption={(item) => ({
            label: item.name,
            value: item.name,
          })}
        />
      ),
      search: {
        transform: (value) => ({
          vaadName: value,
        }),
      },
    },
    {
      title: "Vaad ID",
      dataIndex: "vaadID",
      key: "vaadIDSearch",
      hideInTable: true,
      valueType: "select",
      renderFormItem: (_, props) => (
        <LazySelect
          {...props}
          initialOptions={initialVaadIdOptions}
          fetchOptions={async ({ search, page, pageSize }) => {
            const res = await vaadTableGet.fetch({ page, pageSize, search });
            return (res?.data || []).filter((item) => item.vaadId);
          }}
          mapOption={(item) => ({
            label: String(item.vaadId),
            value: item.id,
          })}
        />
      ),
      search: {
        transform: (value) => ({
          vaadId: value,
        }),
      },
    },
  ];

function ToolBar({ onCreate }: { onCreate: () => void }) {
  const { t } = useTranslation();
  const { download: exportMembers, loading: exportLoading } =
    useUsersExportDownload();

  return [
    <Button
      key="export"
      icon={<IconExport />}
      type="primary"
      ghost
      loading={exportLoading}
      onClick={() => exportMembers({})}
    >
      Export Users
    </Button>,
    <Button
      key="create"
      icon={<PlusCircleOutlined />}
      type="primary"
      onClick={(e) => {
        e.preventDefault();
        onCreate();
      }}
    >
      Add User
    </Button>,
  ];
}

export default function Users(): React.ReactNode | null {
  const navigate = useNavigate();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(() => Date.now());
  // Restore the LazySelect filters from the URL on mount. Their form field
  // names (vaadNameSearch/vaadIDSearch) differ from the transformed query keys
  // (vaadName/vaadId), so syncToUrl can't map them back on its own.
  const [initialFilters, setInitialFilters] = useState<Record<string, string | undefined>>(
    () => {
      const sp = new URLSearchParams(window.location.search);
      return {
        vaadNameSearch: sp.get("vaadName") || undefined,
        vaadIDSearch: sp.get("vaadId") || undefined,
      };
    },
  );
  const { t, i18n } = useTranslation();
  const { setBreadcrumbs } = useBreadcrumbs();
  const usersGet = useUsersTableGet();
  const vaadTableGet = useVaadTableGet();
  const vaadGetById = useVaadGetById();
  // Resolve the human vaadId code for the restored Vaad ID filter so the select
  // shows the code (not the raw vaad UUID stored in the URL).
  const [initialVaadIdOptions, setInitialVaadIdOptions] = useState<
    { label: string; value: string }[] | undefined
  >();
  const [createOpen, setCreateOpen] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role
    ? getUserRequestPath(user.role as UserRoleType) === "admin"
    : false;

  const actions: TableProps<UserTableRow, ParamsType, "text">["actions"] = [
    [
      "edit",
      ({ record }: { record: UserTableRow }) => ({
        title: "Edit user",
        onClick: (e: MouseEvent) => {
          e.stopPropagation();
          navigate(`${record.id}/edit`);
        },
      }),
    ],
  ];

  useEffect(() => {
    setBreadcrumbs([
      { title: <HomeOutlined />, href: "/" },
      { title: t("routes.users") },
    ]);
  }, [i18n.language, t]);

  useEffect(() => {
    const vaadUuid = initialFilters.vaadIDSearch;
    if (!vaadUuid) return;

    vaadGetById.fetch(undefined, vaadUuid).then((vaad) => {
      if (vaad && !(vaad instanceof Error) && vaad.vaadId) {
        setInitialVaadIdOptions([
          { label: String(vaad.vaadId), value: vaadUuid },
        ]);
        // Table memoizes its columns, so remount it once to pick up the newly
        // resolved Vaad ID option (label) for the restored filter.
        setTableKey(Date.now());
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number | undefined;
      current?: number | undefined;
      keyword?: string | undefined;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<UserTableRow>>> => {
    const newParams = queryFilterParams({
      page: current ? `${current}` : "1",
      pageSize: pageSize ? `${pageSize}` : "10",
      ...args,
      ...getSorterParams(sorter),
    });

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });
    return usersGet
      .fetch({
        ...newParams,
        orderBy: "DESC",
        orderByColumn: "createdAt",
        role: undefined,
        roles: newParams.role ? newParams?.role?.split(",") : undefined,
      })
      .then((response) => {
        if (response) {
          const { data, total } = response;

          return {
            data: (data || []).map((item) => ({
              ...item,
              key: item.id,
            })),
            success: true,
            total,
          };
        }

        return { data: [], success: false, total: 0 };
      });
  };

  return (
    <>
      <Table<UserTableRow>
        key={`${tableKey}-${i18n.language}`}
        resetKey={() => {
          setInitialFilters({});
          setInitialVaadIdOptions(undefined);
          setSearchParams({}, { replace: true });
          setTableKey(Date.now());
        }}
        className={clsx("transparent")}
        headerTitle={t("pages.users.tableName")}
        toolBarRender={() => [
          <ToolBar key="toolbar" onCreate={() => setCreateOpen(true)} />,
        ]}
        onRow={(record) => ({
          onClick: () =>
            navigate(`${record.id}/view`, {
              state: { from: location.pathname + location.search },
            }),
          style: { cursor: "pointer" },
        })}
        request={tableRequest}
        columns={columns(t, vaadTableGet, initialVaadIdOptions)}
        form={{ initialValues: initialFilters }}
        {...(isAdmin ? { actions } : {})}
        search={{
          style: {
            // display: 'none',
          },
        }}
        options={{
          search: {
            name: "search",
            style: { width: 280 },
          },
        }}
        columnsState={{
          persistenceKey: "pro-table-users",
          persistenceType: "localStorage",
        }}
      />
      <CreateModal
        open={createOpen}
        mode="create"
        onClose={() => setCreateOpen(false)}
        setTableKey={() => setTableKey(Date.now())}
      />
    </>
  );
}
